import React, { useContext, useEffect, useState } from "react";
import ICatalog_Item from "../../../models/ICatalog_Item";
import styled from "styled-components";
import OwButton from "../../shared/OwButton/OwButton";
import OwTitle from "../../shared/OwTitle/OwTitle";
import { ComponentShortageDetails } from "../ComponentShortageWarning/ComponentShortageWarning";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import { MobilePlusMinusInput } from "../../shared/MobilePlusMinusInput/MobilePlusMinusInput";
import UserContext from "../../../contexts/UserContext";
import OwLabel from "../../shared/OwLabel/OwLabel";
import ICatalog_ItemUI from "../../../models/ICatalog_ItemUI";
import InventoryActionEnum from "../../../models/InventoryActionEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { SocketContext } from "../../../contexts/SocketContext";
import EndpointsEnum from "../../../models/EndpointsEnum";
import { ISupplierModel } from "../../../models/ISupplierModel";
import { Link } from "react-router-dom";
import OwTranslate from "../../shared/OwTranslate/OwTranslate";

type Props = {
    item: ICatalog_Item;
    closeFn: (refresh?: boolean) => void;
    saveFn: (options: {
        catalogItem: ICatalog_Item;
        qtyAdjustment: number;
        adjustmentType: InventoryActionEnum;
        isDetailViewTable?: boolean;
        parentRowIndex?: number;
        selectedWarehouseGuid?: string;
    }) => void;
    supplierModel?: ISupplierModel;
    /** The item's inventory log page, when the user may open it (the desktop row menu's Inventory Log) */
    inventoryLogUrl?: string;
};

export const InventoryItemMobileDetailView = (props: Props) => {
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;

    const [editMode, setEditMode] = useState(false);
    const [qtyAdjustment, setQtyAdjustment] = useState(0);
    const [plusActive, setPlusActive] = useState(true);
    const [saveActive, setSaveActive] = useState(false);

    const finishedAdjustment = () => {
        setEditMode(false);
        setQtyAdjustment(0);
        setSaveActive(false);
        props.closeFn(false);
    };

    const notify = () => {
        setSaveActive(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.WarehouseOnAdjustWarehouseInventory, fn: finishedAdjustment },
        { endpoint: EndpointsEnum.CatalogsOnPerformCatalogCSVInventoryUpdate, fn: finishedAdjustment },
        { endpoint: EndpointsEnum.Notify, fn: notify },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const closeItem = () => {
        props.closeFn();
    };

    const saveAdustment = () => {
        props.saveFn({
            catalogItem: props.item,
            qtyAdjustment,
            adjustmentType: plusActive ? InventoryActionEnum.ADD : InventoryActionEnum.SUBTRACT,
        });
        setSaveActive(true);
    };

    const incrementQty = () => {
        setQtyAdjustment(qtyAdjustment + 1);
    };

    const decrementQty = () => {
        if (qtyAdjustment > 0) {
            setQtyAdjustment(qtyAdjustment - 1);
        }
    };

    const handleQtyAdjustment = (e: React.ChangeEvent<any>) => {
        setQtyAdjustment(Number(e.target.value));
    };

    return (
        <DetailViewWrapper className={"InventoryItemMobileDetailView h-100 w-100 p-3"}>
            <div className={"d-flex flex-column h-100"}>
                <div className={"text-end"}>
                    <OwButton centerIconClass={"fas fa-times"} className={"btn-danger"} fn={closeItem} />
                </div>
                <div>
                    <OwTitle title={props.item.name} tag={"h3"} disableTranslation={true} />
                </div>
                <div className={"text-center mt-3"}>
                    <OwS3ImageViewer
                        catalogItem={props.item}
                        isMicroView={true}
                        attachmentIndex={0}
                        imageIndex={IImageSizeEnum.MEDIUM}
                        className={"me-2 mb-2 mb-md-0"}
                        includeAllImages={true}
                        hideEmptyImage={true}
                    />
                </div>

                {userModel.isSupplierUser ? (
                    <div className={"mb-5"}>
                        {editMode ? (
                            <div className={"text-center"}>
                                <div className={"mt-5 btn-group"}>
                                    <OwButton
                                        btnSizeClass={"btn-lg"}
                                        centerIconClass={"fas fa-plus"}
                                        className={plusActive ? "active btn-primary" : "btn-primary"}
                                        fn={() => setPlusActive(true)}
                                    />
                                    <OwButton
                                        btnSizeClass={"btn-lg"}
                                        centerIconClass={"fas fa-minus"}
                                        className={plusActive ? "btn-primary" : "active btn-primary"}
                                        fn={() => setPlusActive(false)}
                                    />
                                </div>
                                <MobilePlusMinusInput
                                    readOnly={false}
                                    enableIncrement={true}
                                    enableDecrement={qtyAdjustment > 0}
                                    incrementQtyFn={incrementQty}
                                    name={"totalPicked"}
                                    totalValue={qtyAdjustment}
                                    inputFn={handleQtyAdjustment}
                                    decrementQtyFn={decrementQty}
                                    supplierModel={props.supplierModel}
                                />
                                <div className={"mt-3"}>
                                    <OwLabel title={plusActive ? "add" : "remove"} /> {qtyAdjustment}{" "}
                                    <OwLabel title={plusActive ? "to_inventory" : "from_inventory"} />
                                </div>

                                <div className={"mt-3 text-center"}>
                                    <OwButton
                                        btnSizeClass={"btn-lg"}
                                        title={"save"}
                                        className={"btn-success me-3"}
                                        fn={saveAdustment}
                                        active={saveActive}
                                        disabled={qtyAdjustment === 0}
                                        disableOnActive={true}
                                    />
                                    <OwButton
                                        btnSizeClass={"btn-lg"}
                                        className={"btn-danger"}
                                        title={"cancel"}
                                        fn={() => setEditMode(!editMode)}
                                    />
                                </div>
                            </div>
                        ) : null}
                        {editMode ? null : (
                            <div className={"text-center"}>
                                <OwButton
                                    btnSizeClass={"btn-lg"}
                                    centerIconClass={"fas fa-pen"}
                                    className={"btn-primary mt-5"}
                                    fn={() => setEditMode(!editMode)}
                                    title={"adjust_qty"}
                                />
                            </div>
                        )}
                    </div>
                ) : null}
                {props.inventoryLogUrl ? (
                    <div className={"text-center mb-4"}>
                        <Link to={props.inventoryLogUrl} className={"btn btn-secondary btn-lg"} data-testid={"mobile-inventory-log"}>
                            <i className={"fad fa-clipboard-list me-2"} />
                            <OwTranslate id={"view_inventory_log"} defaultMessage={"View Inventory Log"} />
                        </Link>
                    </div>
                ) : null}
                <div className={"mb-3"}>{props.item.sku}</div>
                {props.item.component_shortage && props.item.component_shortage.shortfall > 0 ? (
                    <div className={"mb-3 d-flex text-warning"} data-testid="mobile-component-shortage">
                        <i className={"fas fa-exclamation-triangle me-2 mt-1"} aria-hidden={true} />
                        <ComponentShortageDetails shortage={props.item.component_shortage} />
                    </div>
                ) : null}
                <div className={"mb-3"}>{props.item.description}</div>
                <div>
                    {props.item.row || ""}
                    {props.item.bin || ""}
                </div>
            </div>
        </DetailViewWrapper>
    );
};

const DetailViewWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--main-bg-color);
    z-index: 100;
    overflow-y: scroll;
`;
